// path: frontend/src/components/pages/admin/models/ModelsTable.jsx
import React from "react";
import {
  Pencil,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  UploadCloud,
  ImageIcon,
  FileText,
  SearchIcon,
  Trash2,
  Search,
  X,
} from "lucide-react";
import DeleteModal from "./DeleteModal";
import ToggleSwitch from "../../../ui/ToggleSwitch";

const SUPPORT_OPTIONS = [
  { key: "", label: "All", icon: null },
  {
    key: "imageIn",
    label: "Image In",
    icon: (
      <UploadCloud size={16} className="text-blue-700 dark:text-blue-200" />
    ),
  },
  {
    key: "imageOut",
    label: "Image Out",
    icon: (
      <ImageIcon size={16} className="text-purple-700 dark:text-purple-200" />
    ),
  },
  {
    key: "pdf",
    label: "PDF",
    icon: (
      <FileText size={16} className="text-yellow-700 dark:text-yellow-200" />
    ),
  },
  {
    key: "search",
    label: "Search",
    icon: (
      <SearchIcon size={16} className="text-green-700 dark:text-green-200" />
    ),
  },
];

// Skeleton loader for filter bar, table rows, and pagination
const TableSkeleton = ({ columns = 11, rows = 6 }) => (
  <div>
    {/* Filter bar skeleton */}
    <div className="flex items-end gap-3 mb-4 animate-pulse">
      {/* Search input skeleton */}
      <div className="flex-1 min-w-[200px]">
        <div className="h-4 w-16 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-full bg-bg-40 rounded" />
      </div>
      {/* Filter button skeleton */}
      <div>
        <div className="h-9 w-20 bg-bg-40 rounded" />
      </div>
      {/* Clear button skeleton */}
      <div>
        <div className="h-9 w-16 bg-bg-40 rounded" />
      </div>
      {/* Type dropdown skeleton */}
      <div>
        <div className="h-4 w-12 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-24 bg-bg-40 rounded" />
      </div>
      {/* Provider dropdown skeleton */}
      <div>
        <div className="h-4 w-16 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-24 bg-bg-40 rounded" />
      </div>
      {/* Support dropdown skeleton */}
      <div>
        <div className="h-4 w-16 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-24 bg-bg-40 rounded" />
      </div>
      {/* Add Model button skeleton */}
      <div>
        <div className="h-9 w-28 bg-bg-40 rounded" />
      </div>
    </div>

    {/* Table skeleton */}
    <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
      <table className="min-w-full divide-y divide-bd-50">
        <thead className="bg-bg-40">
          <tr>
            {[...Array(columns)].map((_, i) => (
              <th
                key={i}
                className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
              >
                <div className="h-4 w-20 bg-bg-40 rounded animate-pulse" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-bg-60 divide-y divide-bd-50">
          {[...Array(rows)].map((_, i) => (
            <tr key={i}>
              {[...Array(columns)].map((_, j) => (
                <td key={j} className="px-6 py-4">
                  <div className="h-6 w-full bg-bg-40 rounded animate-pulse" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Pagination skeleton */}
    <div className="flex justify-center items-center gap-1 mt-4 animate-pulse">
      <div className="h-8 w-8 bg-bg-40 rounded" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-8 w-8 bg-bg-40 rounded" />
      ))}
      <div className="h-8 w-8 bg-bg-40 rounded" />
    </div>
  </div>
);

const ModelsTable = ({
  models,
  onEdit,
  searchTerm,
  setSearchTerm,
  activeSearch,
  onFilter,
  onClearSearch,
  typeFilter,
  setTypeFilter,
  providerFilter,
  setProviderFilter,
  typeOptions,
  providerOptions,
  loading,
  supportFilter,
  setSupportFilter,
  onDelete,
  onToggleActive,
  onAddModel,
  currentPage,
  totalPages,
  pagination,
  onPageChange,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [modelToDelete, setModelToDelete] = React.useState(null);
  const [sortField, setSortField] = React.useState(null);
  const [sortDirection, setSortDirection] = React.useState("asc"); // 'asc' or 'desc'

  const handleDeleteClick = (model) => {
    setModelToDelete(model);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (modelToDelete && onDelete) {
      await onDelete(modelToDelete);
    }
    setDeleteModalOpen(false);
    setModelToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setModelToDelete(null);
  };

  // Filter models by support
  const filteredModels = models.filter((model) => {
    if (!supportFilter) return true;
    return model.support && model.support[supportFilter];
  });

  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort filtered models
  const sortedModels = React.useMemo(() => {
    if (!sortField) return filteredModels;

    return [...filteredModels].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested fields
      if (sortField === "createdAt" || sortField === "updatedAt") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (sortField === "isAuto") {
        // Boolean: true comes first when ascending
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      } else if (sortField === "active") {
        // Boolean: true comes first when ascending
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      } else {
        // String comparison (case-insensitive)
        aValue = (aValue || "").toString().toLowerCase();
        bValue = (bValue || "").toString().toLowerCase();
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Compare values
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredModels, sortField, sortDirection]);

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="text-fg-60 opacity-50" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp size={14} className="text-ac-02" />
    ) : (
      <ArrowDown size={14} className="text-ac-02" />
    );
  };

  return (
    <div className="models-table-container">
      {loading ? (
        <TableSkeleton columns={11} rows={6} />
      ) : (
        <>
          {/* Filters and Search */}
          <div className="flex items-end gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Search
              </label>
              <input
                type="text"
                className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="Search by name or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && onFilter()}
                disabled={loading}
              />
            </div>
            <div>
              <button
                onClick={onFilter}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-ac-02 hover:bg-ac-01 text-white dark:text-black rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search size={14} />
                {loading ? "Searching..." : "Filter"}
              </button>
            </div>
            {searchTerm && (
              <div>
                <button
                  onClick={onClearSearch}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-bg-50 hover:bg-bg-40 text-fg-50 border border-bd-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X size={14} />
                  Clear
                </button>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Type
              </label>
              <select
                className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All</option>
                {typeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Provider
              </label>
              <select
                className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
              >
                <option value="">All</option>
                {providerOptions.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Support
              </label>
              <select
                className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02 flex items-center"
                value={supportFilter}
                onChange={(e) => setSupportFilter(e.target.value)}
              >
                {SUPPORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={onAddModel}
                className="px-3 py-1.5 text-xs rounded-md bg-ac-02 text-white dark:text-black font-semibold hover:bg-ac-01 transition-colors"
              >
                Add Model
              </button>
            </div>
          </div>
          <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-bd-50">
              <thead className="bg-bg-40">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      {renderSortIcon("title")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {renderSortIcon("name")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("provider")}
                  >
                    <div className="flex items-center gap-1">
                      Provider
                      {renderSortIcon("provider")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center gap-1">
                      Type
                      {renderSortIcon("type")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("llmClass")}
                  >
                    <div className="flex items-center gap-1">
                      LLM Class
                      {renderSortIcon("llmClass")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("modelType")}
                  >
                    <div className="flex items-center gap-1">
                      Model Type
                      {renderSortIcon("modelType")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("source")}
                  >
                    <div className="flex items-center gap-1">
                      Source
                      {renderSortIcon("source")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Support
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center gap-1">
                      Created
                      {renderSortIcon("createdAt")}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider cursor-pointer hover:bg-bg-50 transition-colors select-none"
                    onClick={() => handleSort("updatedAt")}
                  >
                    <div className="flex items-center gap-1">
                      Updated
                      {renderSortIcon("updatedAt")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-60 divide-y divide-bd-50">
                {sortedModels.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-fg-60">
                      No models found.
                    </td>
                  </tr>
                ) : (
                  sortedModels.map((model) => (
                    <tr key={model._id} className="hover:bg-bg-50/30">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        <div className="flex items-center gap-2">
                          <span>{model.title || "-"}</span>
                          {model.isAuto && (
                            <span
                              className="px-2 py-0.5 rounded-md text-xs font-semibold bg-ac-02 text-white dark:text-black"
                              title="Auto Model"
                            >
                              AUTO
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap font-mono text-xs text-fg-80 max-w-[120px] overflow-hidden text-ellipsis"
                        title={model.name}
                      >
                        <span className="block truncate">{model.name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                        {model.provider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                        {model.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                        {model.llmClass ? (
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-semibold ${model.llmClass === "high" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"}`}
                          >
                            {model.llmClass.charAt(0).toUpperCase() +
                              model.llmClass.slice(1)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                        {model.modelType ? (
                          <span
                            className={`px-2 py-1 rounded-md text-xs font-semibold ${
                              model.modelType === "text"
                                ? "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200"
                                : model.modelType === "image"
                                  ? "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200"
                                  : model.modelType === "animation"
                                    ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-200"
                                    : "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200"
                            }`}
                          >
                            {model.modelType.charAt(0).toUpperCase() +
                              model.modelType.slice(1)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-60">
                        {model.source || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs">
                        {model.support && (
                          <div className="flex items-center gap-2">
                            {model.support.imageIn && (
                              <span
                                title="Image Input"
                                className="inline-flex items-center justify-center w-6 h-6 text-blue-700 dark:text-blue-200"
                              >
                                <UploadCloud size={18} strokeWidth={1.5} />
                              </span>
                            )}
                            {model.support.imageOut && (
                              <span
                                title="Image Output"
                                className="inline-flex items-center justify-center w-6 h-6 text-purple-700 dark:text-purple-200"
                              >
                                <ImageIcon size={18} strokeWidth={1.5} />
                              </span>
                            )}
                            {model.support.pdf && (
                              <span
                                title="PDF"
                                className="inline-flex items-center justify-center w-6 h-6 text-yellow-700 dark:text-yellow-200"
                              >
                                <FileText size={18} strokeWidth={1.5} />
                              </span>
                            )}
                            {model.support.search && (
                              <span
                                title="Web Search"
                                className="inline-flex items-center justify-center w-6 h-6 text-green-700 dark:text-green-200"
                              >
                                <SearchIcon size={18} strokeWidth={1.5} />
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-fg-60">
                        {model.createdAt
                          ? new Date(model.createdAt).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-fg-60">
                        {model.updatedAt
                          ? new Date(model.updatedAt).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <ToggleSwitch
                            checked={model.active === true}
                            onChange={async (isChecked) => {
                              if (onToggleActive) {
                                await onToggleActive(model._id, isChecked);
                              }
                            }}
                          />
                          <button
                            className="p-1.5 rounded hover:bg-bg-40"
                            title="Edit Model"
                            onClick={() => onEdit(model)}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded hover:bg-bg-40 group"
                            title="Delete Model"
                            onClick={() => handleDeleteClick(model)}
                          >
                            <Trash2
                              size={16}
                              className="text-fg-50 group-hover:text-red-600 transition-colors"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && sortedModels.length > 0 && totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                className="p-1.5 rounded hover:bg-bg-40 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                aria-label="Previous page"
              >
                <ChevronLeft size={20} />
              </button>

              {(() => {
                const pages = [];
                const current = currentPage;

                // Always show first page
                pages.push(1);

                if (totalPages <= 7) {
                  // Show all pages if 7 or less
                  for (let i = 2; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  // Show ellipsis logic
                  if (current <= 3) {
                    // Near start: 1 2 3 4 ... last
                    for (let i = 2; i <= 4; i++) pages.push(i);
                    pages.push("...");
                    pages.push(totalPages);
                  } else if (current >= totalPages - 2) {
                    // Near end: 1 ... n-3 n-2 n-1 n
                    pages.push("...");
                    for (let i = totalPages - 3; i <= totalPages; i++)
                      pages.push(i);
                  } else {
                    // Middle: 1 ... current-1 current current+1 ... last
                    pages.push("...");
                    pages.push(current - 1);
                    pages.push(current);
                    pages.push(current + 1);
                    pages.push("...");
                    pages.push(totalPages);
                  }
                }

                return pages.map((page, idx) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-3 py-1.5 text-fg-60"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        page === current
                          ? "bg-ac-02 text-white"
                          : "hover:bg-bg-40 text-fg-60"
                      }`}
                      onClick={() => onPageChange(page)}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  ),
                );
              })()}

              <button
                className="p-1.5 rounded hover:bg-bg-40 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                aria-label="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete Modal - Always rendered outside loading condition */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Model"
        description={
          modelToDelete
            ? `Are you sure you want to delete the model "${modelToDelete.title || modelToDelete.name}"? This action cannot be undone.`
            : ""
        }
        confirmText="Delete"
        confirmColor="red"
      />
    </div>
  );
};

export default ModelsTable;
